/**
 * EditAddressModal - Fun dog-themed modal to edit address fields
 * Preserves order_attributes when updating
 */
import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Stack,
  Alert,
  keyframes,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { QuizButton } from "./shared/QuizButton";
import { ACCOUNT_COLORS } from "../theme/colors";
import type { AddressData } from "../api/accountProxy";
import { QUIZ } from "@/theme/quizTokens";

// Paw print SVG path
const PAW_PATH = "M12 2C9.5 2 7.5 4 7.5 6.5C7.5 9 9.5 11 12 11C14.5 11 16.5 9 16.5 6.5C16.5 4 14.5 2 12 2ZM4.5 9C2.5 9 1 10.5 1 12.5C1 14.5 2.5 16 4.5 16C6.5 16 8 14.5 8 12.5C8 10.5 6.5 9 4.5 9ZM19.5 9C17.5 9 16 10.5 16 12.5C16 14.5 17.5 16 19.5 16C21.5 16 23 14.5 23 12.5C23 10.5 21.5 9 19.5 9ZM6 17C4 17 2.5 18.5 2.5 20.5C2.5 22.5 4 24 6 24C8 24 9.5 22.5 9.5 20.5C9.5 18.5 8 17 6 17ZM18 17C16 17 14.5 18.5 14.5 20.5C14.5 22.5 16 24 18 24C20 24 21.5 22.5 21.5 20.5C21.5 18.5 20 17 18 17Z";

// Bone SVG path
const BONE_PATH = "M3.5 5.5C3.5 4.12 4.62 3 6 3C7.08 3 8 3.67 8.35 4.6C8.57 4.54 8.78 4.5 9 4.5C10.1 4.5 11 5.4 11 6.5V7H13V6.5C13 5.4 13.9 4.5 15 4.5C15.22 4.5 15.43 4.54 15.65 4.6C16 3.67 16.92 3 18 3C19.38 3 20.5 4.12 20.5 5.5C20.5 6.5 19.92 7.35 19.08 7.78C19.35 8.14 19.5 8.57 19.5 9C19.5 10.1 18.6 11 17.5 11H17V13H17.5C18.6 13 19.5 13.9 19.5 15C19.5 15.43 19.35 15.86 19.08 16.22C19.92 16.65 20.5 17.5 20.5 18.5C20.5 19.88 19.38 21 18 21C16.92 21 16 20.33 15.65 19.4C15.43 19.46 15.22 19.5 15 19.5C13.9 19.5 13 18.6 13 17.5V17H11V17.5C11 18.6 10.1 19.5 9 19.5C8.78 19.5 8.57 19.46 8.35 19.4C8 20.33 7.08 21 6 21C4.62 21 3.5 19.88 3.5 18.5C3.5 17.5 4.08 16.65 4.92 16.22C4.65 15.86 4.5 15.43 4.5 15C4.5 13.9 5.4 13 6.5 13H7V11H6.5C5.4 11 4.5 10.1 4.5 9C4.5 8.57 4.65 8.14 4.92 7.78C4.08 7.35 3.5 6.5 3.5 5.5Z";

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-8px) rotate(5deg); }
`;

const wiggle = keyframes`
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
`;

type Address = {
  id: string | number;
  first_name?: string;
  last_name?: string;
  address1?: string;
  address2?: string | null;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  phone?: string | null;
};

type EditAddressModalProps = {
  open: boolean;
  onClose: () => void;
  address: Address | null;
  onSave: (addressId: string, addressData: AddressData) => Promise<void>;
};

// Decorative paw print component
function DecorativePaw({
  size = 24,
  color = "rgba(123, 51, 6, 0.08)",
  style
}: {
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}) {
  return (
    <Box sx={{ position: "absolute", ...style }}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
        <path d={PAW_PATH} />
      </svg>
    </Box>
  );
}

// Styled input component
function StyledInput({
  label,
  value,
  onChange,
  placeholder,
  required,
  disabled,
  halfWidth,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  halfWidth?: boolean;
  error?: string;
}) {
  const hasError = Boolean(error);

  return (
    <Box sx={{ flex: halfWidth ? 1 : "auto" }}>
      <Typography
        component="label"
        sx={{
          display: "block",
          fontSize: 13,
          fontWeight: 700,
          color: hasError ? ACCOUNT_COLORS.error : ACCOUNT_COLORS.textPrimary,
          mb: 0.75,
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: ACCOUNT_COLORS.error, ml: 0.5 }}>
            *
          </Box>
        )}
      </Typography>
      <Box
        component="input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        sx={{
          width: "100%",
          px: 2.5,
          py: 1.75,
          fontSize: 15,
          fontWeight: 500,
          fontFamily: "inherit",
          color: ACCOUNT_COLORS.textPrimary,
          background: hasError
            ? "linear-gradient(135deg, #FFF5F5 0%, #FFE8E8 100%)"
            : "linear-gradient(135deg, #FFFFFF 0%, #F8FBFF 100%)",
          border: `3px solid ${hasError ? ACCOUNT_COLORS.error : "rgba(123, 51, 6, 0.15)"}`,
          borderRadius: "50px",
          outline: "none",
          transition: "all 0.25s ease",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04), inset 0 1px 2px rgba(255,255,255,0.8)",
          "&:hover:not(:disabled)": {
            borderColor: hasError ? ACCOUNT_COLORS.error : "rgba(123, 51, 6, 0.3)",
            transform: "translateY(-1px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08), inset 0 1px 2px rgba(255,255,255,0.8)",
          },
          "&:focus": {
            borderColor: hasError ? ACCOUNT_COLORS.error : ACCOUNT_COLORS.primary,
            transform: "translateY(-1px)",
            boxShadow: hasError
              ? `0 4px 12px rgba(255,120,115,0.2), 0 0 0 4px ${ACCOUNT_COLORS.error}15`
              : `0 4px 12px rgba(123,51,6,0.15), 0 0 0 4px ${ACCOUNT_COLORS.primary}15`,
          },
          "&:disabled": {
            background: "linear-gradient(135deg, #E9DFD7 0%, #EBEBEB 100%)",
            color: ACCOUNT_COLORS.textDisabled,
            cursor: "not-allowed",
            transform: "none",
            boxShadow: "none",
          },
          "&::placeholder": {
            color: ACCOUNT_COLORS.textTertiary,
            opacity: 0.6,
            fontWeight: 400,
          },
        }}
      />
      {hasError && (
        <Typography
          sx={{
            fontSize: 12,
            color: ACCOUNT_COLORS.error,
            mt: 0.5,
            fontWeight: 500,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}

// Section header component
function SectionHeader({
  icon,
  title,
}: {
  icon: React.ReactNode;
  title: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mb: 2,
        pb: 1.5,
        borderBottom: `2px dashed ${ACCOUNT_COLORS.borderLight}`,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          background: `linear-gradient(135deg, ${ACCOUNT_COLORS.coralLight} 0%, ${ACCOUNT_COLORS.coral} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: ACCOUNT_COLORS.primary,
          boxShadow: "0 2px 8px rgba(123, 51, 6, 0.15)",
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 800,
          color: ACCOUNT_COLORS.textPrimary,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

export function EditAddressModal({
  open,
  onClose,
  address,
  onSave,
}: EditAddressModalProps) {
  const [formData, setFormData] = useState<AddressData>({
    first_name: "",
    last_name: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    zip: "",
    country: "",
    phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof AddressData, string>>>({});
  const [success, setSuccess] = useState(false);

  // Check if form is valid (all required fields filled)
  const isFormValid = useMemo(() => {
    return (
      formData.first_name?.trim() &&
      formData.address1?.trim() &&
      formData.city?.trim() &&
      formData.zip?.trim()
    );
  }, [formData]);

  // Reset form when modal opens with address data
  useEffect(() => {
    if (open && address) {
      setFormData({
        first_name: address.first_name || "",
        last_name: address.last_name || "",
        address1: address.address1 || "",
        address2: address.address2 || "",
        city: address.city || "",
        province: address.province || "",
        zip: address.zip || "",
        country: address.country || "United Kingdom",
        phone: address.phone || "",
      });
      setError(null);
      setFieldErrors({});
      setSuccess(false);
      setSaving(false);
    }
  }, [open, address]);

  const handleChange = (field: keyof AddressData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    // Clear general error
    if (error) {
      setError(null);
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof AddressData, string>> = {};

    if (!formData.first_name?.trim()) {
      errors.first_name = "First name is required";
    }
    if (!formData.address1?.trim()) {
      errors.address1 = "Address is required";
    }
    if (!formData.city?.trim()) {
      errors.city = "City is required";
    }
    if (!formData.zip?.trim()) {
      errors.zip = "Postcode is required";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!address) return;

    // Validate all fields
    if (!validateForm()) {
      setError("Please fill in all required fields");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(String(address.id), formData);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err?.message || "Failed to update address");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "20px" },
          maxHeight: { xs: "100%", sm: "90vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2 },
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(180deg, #8EE5FF 0%, #8EE5FF 50%, #8EE5FF 100%)",
          overflow: "hidden",
          position: "relative",
        },
      }}
    >
      {/* Decorative paw prints */}
      <DecorativePaw size={40} style={{ top: 80, right: 20, transform: "rotate(25deg)" }} />
      <DecorativePaw size={28} style={{ top: 180, left: 15, transform: "rotate(-15deg)" }} />
      <DecorativePaw size={32} style={{ bottom: 150, right: 30, transform: "rotate(45deg)" }} />
      <DecorativePaw size={24} style={{ bottom: 80, left: 25, transform: "rotate(-30deg)" }} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: { xs: 2, md: 3 },
          background: "linear-gradient(135deg, #4A3026 0%, #9A4A1C 100%)",
          flexShrink: 0,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative bones in header */}
        <Box
          sx={{
            position: "absolute",
            right: 60,
            top: "50%",
            transform: "translateY(-50%)",
            opacity: 0.15,
            animation: `${wiggle} 3s ease-in-out infinite`,
          }}
        >
          <svg width={50} height={50} viewBox="0 0 24 24" fill={QUIZ.color.white}>
            <path d={BONE_PATH} />
          </svg>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, zIndex: 1 }}>
          {/* Animated house icon */}
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              background: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: `${float} 3s ease-in-out infinite`,
            }}
          >
            <Box
              component="span"
              sx={{ fontSize: 28 }}
            >
              🏠
            </Box>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 22 },
                fontWeight: 900,
                color: QUIZ.color.white,
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            >
              Edit Address
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: "rgba(255,255,255,0.8)",
              }}
            >
              Where should we deliver your goodies?
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{
            color: QUIZ.color.white,
            background: "rgba(255,255,255,0.15)",
            zIndex: 1,
            "&:hover": { background: "rgba(255,255,255,0.25)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2, md: 3 }, position: "relative", zIndex: 1 }}>
        {/* Success State */}
        {success && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{
              borderRadius: "14px",
              mb: 2,
              background: ACCOUNT_COLORS.green,
              color: ACCOUNT_COLORS.textPrimary,
              fontWeight: 600,
              "& .MuiAlert-icon": { alignItems: "center", color: ACCOUNT_COLORS.primary },
            }}
          >
            Woof! Address updated successfully! 🐕
          </Alert>
        )}

        {/* Error State */}
        {error && (
          <Alert
            severity="error"
            sx={{
              borderRadius: "14px",
              mb: 2,
              "& .MuiAlert-icon": { alignItems: "center" },
            }}
          >
            {error}
          </Alert>
        )}

        {!success && (
          <Stack spacing={2.5}>
            {/* Contact Section */}
            <Box
              sx={{
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                p: { xs: 2, md: 2.5 },
                border: "2px solid rgba(255,255,255,0.5)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <SectionHeader
                icon={<Box component="span" sx={{ fontSize: 18 }}>👤</Box>}
                title="Who's receiving?"
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <StyledInput
                  label="First Name"
                  value={formData.first_name || ""}
                  onChange={handleChange("first_name")}
                  placeholder="John"
                  required
                  disabled={saving}
                  halfWidth
                  error={fieldErrors.first_name}
                />
                <StyledInput
                  label="Last Name"
                  value={formData.last_name || ""}
                  onChange={handleChange("last_name")}
                  placeholder="Smith"
                  disabled={saving}
                  halfWidth
                />
              </Stack>
            </Box>

            {/* Address Section */}
            <Box
              sx={{
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                p: { xs: 2, md: 2.5 },
                border: "2px solid rgba(255,255,255,0.5)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <SectionHeader
                icon={<Box component="span" sx={{ fontSize: 18 }}>📍</Box>}
                title="Delivery Location"
              />
              <Stack spacing={2}>
                <StyledInput
                  label="Address Line 1"
                  value={formData.address1 || ""}
                  onChange={handleChange("address1")}
                  placeholder="123 High Street"
                  required
                  disabled={saving}
                  error={fieldErrors.address1}
                />
                <StyledInput
                  label="Address Line 2"
                  value={formData.address2 || ""}
                  onChange={handleChange("address2")}
                  placeholder="Flat 4, Building Name (optional)"
                  disabled={saving}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <StyledInput
                    label="City"
                    value={formData.city || ""}
                    onChange={handleChange("city")}
                    placeholder="London"
                    required
                    disabled={saving}
                    halfWidth
                    error={fieldErrors.city}
                  />
                  <StyledInput
                    label="County"
                    value={formData.province || ""}
                    onChange={handleChange("province")}
                    placeholder="Greater London"
                    disabled={saving}
                    halfWidth
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <StyledInput
                    label="Postcode"
                    value={formData.zip || ""}
                    onChange={handleChange("zip")}
                    placeholder="SW1A 1AA"
                    required
                    disabled={saving}
                    halfWidth
                    error={fieldErrors.zip}
                  />
                  <StyledInput
                    label="Country"
                    value={formData.country || ""}
                    onChange={handleChange("country")}
                    placeholder="United Kingdom"
                    disabled={saving}
                    halfWidth
                  />
                </Stack>
              </Stack>
            </Box>

            {/* Phone Section */}
            <Box
              sx={{
                background: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                p: { xs: 2, md: 2.5 },
                border: "2px solid rgba(255,255,255,0.5)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
              }}
            >
              <SectionHeader
                icon={<Box component="span" sx={{ fontSize: 18 }}>📱</Box>}
                title="Contact Number"
              />
              <StyledInput
                label="Phone Number"
                value={formData.phone || ""}
                onChange={handleChange("phone")}
                placeholder="07123 456789"
                disabled={saving}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  color: ACCOUNT_COLORS.textTertiary,
                  mt: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box component="span">🐾</Box>
                We'll text you when your pup's food is on the way!
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Footer */}
      {!success && (
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(10px)",
            borderTop: "2px solid rgba(255,255,255,0.5)",
            flexShrink: 0,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <QuizButton
              variant="secondary"
              onClick={onClose}
              disabled={saving}
              fullWidth
            >
              Cancel
            </QuizButton>
            <QuizButton
              variant="primary"
              onClick={handleSave}
              disabled={saving || !isFormValid}
              fullWidth
            >
              {saving ? "Saving..." : "Save Changes 🐕"}
            </QuizButton>
          </Stack>

          {/* Hint when button is disabled */}
          {!isFormValid && !saving && (
            <Typography
              sx={{
                fontSize: 12,
                color: ACCOUNT_COLORS.textTertiary,
                textAlign: "center",
                mt: 1.5,
              }}
            >
              Fill in all required fields to save
            </Typography>
          )}
        </Box>
      )}
    </Dialog>
  );
}
